/**
 * This component contains code to upload software. once we select a file.
 * A form will appear to provide basic information related to the selected software
 */
import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CardHeader,
  IconButton,
  styled,
  Grid,
  TextField,
  InputLabel,
  MenuItem,
  Checkbox,
  ListItemText,
  Stack
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DashedDivider from "common/ui/DashedDivider";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Typography from "@mui/material/Typography/Typography";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "context/hooks";
import { setUploadSoftware } from "context/modal.context";
import { selectUploadSoftwareProps } from "context/modal.selector";
import { useUploadSoftware } from "services/softwares.service";
import { Controller } from "react-hook-form";
import useForm from "hooks/useForm";
import yup from "helpers/validator";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  useMachineHardwareVersion,
  useMachineUses
} from "services/machines.service";
import { toast } from "react-hot-toast";
import SoftwarePackageCategory from "types/SoftwarePackageCategory.enum";
import MachineUse from "types/MachineType.enum";
import { AxiosProgressEvent } from "axios";
import UploadProgress from "types/UploadProgress.enum";
import { useQueryClient } from "@tanstack/react-query";
import LoadingButton from "common/ui/LoadingButton";
import "./UploadSoftware.scss";
import { encode } from "utils/common";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1
});

const defaultValues = {
  file: null,
  softwarePackageTypeId: "",
  machineUseId: [],
  softwarePackageName: "",
  softwareVersion: "",
  softwarePartId: "",
  softwareConfiguration: "",
  hardwareVersionId: [],
  description: ""
};

function UploadSoftware() {
  const { t } = useTranslation();
  const props = useAppSelector(selectUploadSoftwareProps);
  const uploadSoftware = useUploadSoftware(onSuccessfulUpload);
  const dispatch = useAppDispatch();
  const machineUses = useMachineUses();
  const machineHardwareVersion = useMachineHardwareVersion();
  const queryClient = useQueryClient();
  const [progress, setProgress] = React.useState({
    status: UploadProgress.idle,
    value: 0
  });

  const softwarePackageTypes = Object.entries(SoftwarePackageCategory).map(
    ([label, id]) => ({ id, label })
  );

  const uploadFormSchema = yup.object().shape({
    file: yup.mixed(),
    softwarePackageTypeId: yup
      .string()
      .required(t("uploadSoftware.packageTypeRequired")),
    machineUseId: yup.array().min(1, t("uploadSoftware.machineUseRequired")),
    softwarePackageName: yup
      .string()
      .transform((val) => val?.trim())
      .required(t("uploadSoftware.packageNameRequired")),
    softwareVersion: yup
      .string()
      .transform((val) => val?.trim())
      .required(t("uploadSoftware.versionRequired")),
    softwarePartId: yup
      .string()
      .transform((val) => val?.trim())
      .max(100, t("uploadSoftware.softwarePartIdLimit"))
      .when("machineUseId", ([machineUseIds], schema) => {
        if (machineUseIds.some((id) => id === MachineUse.clinical)) {
          return schema.required(t("uploadSoftware.partIdRequired"));
        }
      }),
    softwareConfiguration: yup
      .string()
      .transform((val) => val?.trim())
      .max(100, t("uploadSoftware.softwareConfigurationLimit"))
      .when("machineUseId", ([machineUseIds], schema) => {
        if (machineUseIds.some((id) => id === MachineUse.clinical)) {
          return schema.required(t("uploadSoftware.configRequired"));
        }
      }),
    hardwareVersionId: yup
      .array()
      .min(1, t("uploadSoftware.hardwareVersionRequired")),
    description: yup
      .string()
      .transform((val) => val?.trim())
      .max(500, t("uploadSoftware.softwareDescriptionLimit"))
      .transform((val) => encode(val))
  });

  const {
    register,
    reset,
    handleSubmit,
    watch,
    formState: { errors },
    control
  } = useForm({
    defaultValues,
    resolver: yupResolver(uploadFormSchema)
  });

  const software = watch("file");
  const machineUse = watch("machineUseId");
  const isClinicalSoftware = machineUse.some(
    (id) => id === MachineUse.clinical
  );

  function dismiss() {
    setProgress({ status: UploadProgress.idle, value: 0 });
    reset();
    dispatch(setUploadSoftware({ open: false }));
  }

  function onSuccessfulUpload(_data, error) {
    queryClient.invalidateQueries({
      queryKey: ["software", "list"],
      exact: false
    });
    if (!error) {
      setProgress({ status: UploadProgress.completed, value: 100 });
    } else {
      setProgress({ status: UploadProgress.idle, value: 0 });
    }
  }

  function onSubmit(data) {
    const file = data.file[0];

    if (file.name?.match(/\.(zip)$/i)) {
      setProgress({
        status: UploadProgress.uploading,
        value: 0
      });
      uploadSoftware.mutate({
        data: { ...data, file },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          let percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setProgress({
            status: UploadProgress.uploading,
            value: percentCompleted
          });
        }
      });
    } else {
      toast.error(t("uploadSoftware.invalidFile"));
    }
  }

  return (
    <Dialog open={props.open} maxWidth="sm" fullWidth scroll="body">
      <DialogTitle
        component={CardHeader}
        avatar={<CloudUploadIcon color="primary" />}
        title={t("uploadSoftware.uploadSoftware")}
        titleTypographyProps={{
          variant: "h6",
          fontWeight: "medium",
          color: "primary.main"
        }}
        action={
          <IconButton
            onClick={dismiss}
            disabled={progress.status === UploadProgress.uploading}
          >
            <CloseIcon />
          </IconButton>
        }
      />
      <DashedDivider style={{ width: "93%" }} />
      {progress.status === UploadProgress.idle ? (
        <>
          <DialogContent>
            <Typography
              variant="subtitle1"
              alignSelf="center"
              textAlign="center"
            >
              {t("uploadSoftware.selectUpload")}
            </Typography>
          </DialogContent>
          <DashedDivider style={{ width: "93%" }} />
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogContent sx={{ py: 2 }}>
              <Stack alignItems="center">
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                >
                  <Typography variant="subtitle1">
                    {t("uploadSoftware.browseFiles")}
                  </Typography>
                  <Controller
                    control={control}
                    name="file"
                    render={({ field: { value, onChange, ...field } }) => {
                      return (
                        <VisuallyHiddenInput
                          type="file"
                          {...field}
                          value={value?.fileName}
                          onChange={(e) => {
                            if (e.target.files.length > 0) {
                              onChange(e.target.files);
                            }
                          }}
                        />
                      );
                    }}
                  />
                </Button>
                {software?.length > 0 ? (
                  <Typography
                    alignSelf="center"
                    noWrap
                    title={software[0].name}
                    width="90%"
                    textAlign="center"
                    pt={1}
                  >
                    {t("uploadSoftware.selectedFile", {
                      fileName: software[0].name
                    })}
                  </Typography>
                ) : null}
              </Stack>
            </DialogContent>
            {software?.length > 0 ? (
              <>
                <DashedDivider style={{ width: "93%" }} />
                <DialogContent sx={{ pt: 0 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <InputLabel htmlFor="softwarePackageTypeId" required>
                        {t("uploadSoftware.softwarePackageType")}
                      </InputLabel>
                      <Controller
                        control={control}
                        name="softwarePackageTypeId"
                        render={({ field }) => {
                          return (
                            <TextField
                              {...field}
                              id="softwarePackageTypeId"
                              type="text"
                              fullWidth
                              size="small"
                              error={!!errors.softwarePackageTypeId}
                              helperText={errors.softwarePackageTypeId?.message}
                              select
                              SelectProps={{ displayEmpty: true }}
                              inputProps={{
                                "aria-label": "Select Software Package Type"
                              }}
                            >
                              <MenuItem value="">{t("common.select")}</MenuItem>
                              {softwarePackageTypes.map((type) => (
                                <MenuItem key={type.id} value={type.id}>
                                  {type.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          );
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <InputLabel htmlFor="machineUseId" required>
                        {t("uploadSoftware.machineUseType")}
                      </InputLabel>
                      <Controller
                        control={control}
                        name="machineUseId"
                        render={({ field: { value, onChange } }) => {
                          return (
                            <TextField
                              id="machineUseId"
                              fullWidth
                              type="text"
                              size="small"
                              value={value}
                              onChange={(event) => {
                                const value = event.target.value;
                                onChange(
                                  typeof value === "string"
                                    ? value.split(",")
                                    : value
                                );
                              }}
                              error={!!errors.machineUseId}
                              helperText={errors.machineUseId?.message}
                              select
                              inputProps={{
                                "aria-label": "Select Machine Use Type"
                              }}
                              SelectProps={{
                                displayEmpty: true,
                                multiple: true,
                                renderValue: (selected: string[]) => {
                                  if (selected.length === 0) {
                                    return t("common.select");
                                  }

                                  return selected
                                    .map(
                                      (id) =>
                                        machineUses.data.find(
                                          (use) => use.id === id
                                        )?.name
                                    )
                                    .join(", ");
                                }
                              }}
                            >
                              {machineUses.data.map((machineUse) => (
                                <MenuItem
                                  disableGutters
                                  key={machineUse.id}
                                  value={machineUse.id}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={value.indexOf(machineUse.id) > -1}
                                  />
                                  <ListItemText primary={machineUse.name} />
                                </MenuItem>
                              ))}
                            </TextField>
                          );
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <InputLabel htmlFor="softwarePackageName" required>
                        {t("uploadSoftware.softwarePackageName")}
                      </InputLabel>
                      <TextField
                        id="softwarePackageName"
                        {...register("softwarePackageName")}
                        type="text"
                        fullWidth
                        size="small"
                        error={!!errors.softwarePackageName}
                        helperText={errors.softwarePackageName?.message}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <InputLabel htmlFor="softwareVersion" required>
                        {t("uploadSoftware.softwareVersion")}
                      </InputLabel>
                      <TextField
                        id="softwareVersion"
                        {...register("softwareVersion")}
                        type="text"
                        fullWidth
                        size="small"
                        error={!!errors.softwareVersion}
                        helperText={errors.softwareVersion?.message}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <InputLabel
                        htmlFor="softwarePartId"
                        required={isClinicalSoftware}
                      >
                        {t("uploadSoftware.softwarePartID")}
                      </InputLabel>
                      <TextField
                        id="softwarePartId"
                        {...register("softwarePartId")}
                        type="text"
                        fullWidth
                        size="small"
                        error={!!errors.softwarePartId}
                        helperText={errors.softwarePartId?.message}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <InputLabel
                        htmlFor="softwareConfiguration"
                        required={isClinicalSoftware}
                      >
                        {t("uploadSoftware.softwareConfiguration")}
                      </InputLabel>
                      <TextField
                        id="softwareConfiguration"
                        {...register("softwareConfiguration")}
                        type="text"
                        fullWidth
                        size="small"
                        error={!!errors.softwareConfiguration}
                        helperText={errors.softwareConfiguration?.message}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <InputLabel htmlFor="hardwareVersionId" required>
                        {t("uploadSoftware.hardwareVersions")}
                      </InputLabel>
                      <Controller
                        control={control}
                        name="hardwareVersionId"
                        render={({ field: { value, onChange } }) => {
                          return (
                            <TextField
                              id="hardwareVersionId"
                              type="text"
                              fullWidth
                              size="small"
                              value={value}
                              onChange={(event) => {
                                const value = event.target.value;
                                onChange(
                                  typeof value === "string"
                                    ? value.split(",")
                                    : value
                                );
                              }}
                              error={!!errors.hardwareVersionId}
                              helperText={errors.hardwareVersionId?.message}
                              select
                              inputProps={{
                                "aria-label": "Select Hardware Versions"
                              }}
                              SelectProps={{
                                displayEmpty: true,
                                multiple: true,
                                renderValue: (selected: string[]) => {
                                  if (selected.length === 0) {
                                    return t("common.select");
                                  }

                                  return selected
                                    .map(
                                      (id) =>
                                        machineHardwareVersion.data.find(
                                          (hardware) => hardware.id === id
                                        )?.name
                                    )
                                    .join(", ");
                                }
                              }}
                            >
                              {machineHardwareVersion.data.map((hardware) => (
                                <MenuItem
                                  disableGutters
                                  key={hardware.id}
                                  value={hardware.id}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={value.indexOf(hardware.id) > -1}
                                  />
                                  <ListItemText primary={hardware.name} />
                                </MenuItem>
                              ))}
                            </TextField>
                          );
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <InputLabel htmlFor="description">
                        {t("uploadSoftware.softwareDescription")}
                      </InputLabel>
                      <TextField
                        id="description"
                        {...register("description")}
                        type="text"
                        multiline
                        rows={4}
                        fullWidth
                        size="small"
                        error={!!errors.description}
                        helperText={errors.description?.message}
                      />
                    </Grid>
                  </Grid>
                </DialogContent>
              </>
            ) : null}
            <DashedDivider style={{ width: "93%" }} />
            <DialogActions sx={{ justifyContent: "center", padding: 2 }}>
              <Button variant="outlined" color="gray" onClick={dismiss}>
                {t("common.cancel")}
              </Button>
              {software?.length > 0 ? (
                <Button variant="outlined" type="submit">
                  {t("uploadSoftware.addSoftware")}
                </Button>
              ) : null}
            </DialogActions>
          </form>
        </>
      ) : (
        <>
          <DialogContent>
            <Typography
              variant="subtitle1"
              alignSelf="center"
              color="primary.main"
              align="center"
              fontWeight="medium"
            >
              {progress.status === UploadProgress.completed
                ? t("uploadSoftware.uploadComplete")
                : t("uploadSoftware.uploading")}
            </Typography>
            {progress.status === UploadProgress.uploading ? (
              <Stack
                direction="row"
                className="upload-software-modal__progress"
                spacing={1}
                px={2}
                py={1}
              >
                <CloudUploadIcon color="primary" fontSize="small" />
                <Typography variant="body2">
                  {t("uploadSoftware.progress", { count: progress.value })}
                </Typography>
              </Stack>
            ) : null}
          </DialogContent>
          <DashedDivider style={{ width: "93%" }} />
          <DialogActions sx={{ justifyContent: "center", padding: 2 }}>
            <LoadingButton
              variant="outlined"
              onClick={dismiss}
              loading={progress.status === UploadProgress.uploading}
            >
              {t("common.ok")}
            </LoadingButton>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

export default UploadSoftware;
